import React, { useRef, useState } from "react";
import "./FileUploader.css";
import Button from "components/button/Button";
import { FaCloudUploadAlt, FaMusic } from "react-icons/fa";
import TermsAndConditions from "components/TermsAndCondition/TermsAndConditions";

interface FileUploaderProps {
  onUpload: (file: File | null) => void;
}

const SUPPORTED_FORMATS = [
  "audio/mpeg",
  "audio/wav",
  "audio/mp4",
  "audio/ogg",
  "audio/aac",
  "audio/x-aiff",
];

const FileUploader: React.FC<FileUploaderProps> = ({ onUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const toggleTerms = () => {
    setIsTermsOpen(!isTermsOpen);
  };

  // Reference to the hidden file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setIsChecked(false);
    setError(null); // Clear any previous errors

    const file = e.dataTransfer.files[0];

    // Check if the file is of a supported format
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setError("Unsupported file format. Please upload a playable audio file.");
      setDroppedFile(null);
      return;
    }

    // Set the file if it passes validation
    setDroppedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;

    if (file) {
      // Check if the file is of a supported format
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        setError("Unsupported file format. Please upload a playable audio file.");
        setDroppedFile(null);
        return;
      }

      setDroppedFile(file);
      setError(null); // Clear any errors
    }
  };

  const onUploadClicked = () => {
    if (droppedFile) {
      onUpload(droppedFile);
    } else {
      setError("Please upload a playable audio file.");
    }
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const openFileExplorer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger the click
    }
  };

  return (
    <div
      className={`upload-container ${isDragging ? "dragging" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept={SUPPORTED_FORMATS.join(",")}
        onChange={handleFileChange}
      />

      {!droppedFile && !error && (
        <div onClick={openFileExplorer} style={{ textAlign: "center" }}>
          <FaCloudUploadAlt size={50} color="#ffc107" />
          <h2 className="upload-title">Upload Track</h2>
          <p className="upload-subtitle">
            Drag and drop your file here or click to upload
          </p>
          <p className="upload-subtitle">
            Accepted file formats: MP3, M4A, WAV, AAC, OGG, AIFF
          </p>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      {droppedFile && (
        <>
          <div className="file-name">
            <div>
              <FaMusic size={18} color="#ffc107" />
            </div>
            <div>{droppedFile.name}</div>
          </div>
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="terms"
              checked={isChecked}
              onChange={handleCheckboxChange}
            />
            <div className="checkbox-label">
              I accept the
              <span className="terms" onClick={toggleTerms}>
                Terms and Conditions
              </span>
            </div>
          </div>
        </>
      )}

      <div className="upload-btn">
        {isChecked && droppedFile && (
          <Button onClick={onUploadClicked} variant="primary" label="Upload" />
        )}
      </div>
      {isTermsOpen && <TermsAndConditions onClose={toggleTerms} />}
    </div>
  );
};

export default FileUploader;
